import { rem, Stack, TextInput } from "@mantine/core";
import { IconArrowDown, IconSearch } from "@tabler/icons-react";
import { List, ThemeIcon } from "@mantine/core";
import { Table } from "@mantine/core";
import { formatCurrencyFromPence } from "../../../utils";
import { uniqueId } from "lodash";
import { iconHeight, negativeIcon, neutralIcon, positiveIcon } from "./Icons";
import { PresentationalTransaction } from "./Projector";
import { useState } from "react";

type Txn = PresentationalTransaction["transactions"][number];

// "J salary (outstanding holiday)" -> base "J salary".
const baseNameOf = (name: string) => {
  const i = name.indexOf(" (");
  return i === -1 ? name : name.slice(0, i);
};

// Sub-line label for a component, e.g. "outstanding holiday"; the plain salary
// entry (no suffix) is just "salary".
const labelFor = (base: string, name: string) => {
  if (name === base) return "salary";
  return name
    .slice(base.length)
    .trim()
    .replace(/^\(/, "")
    .replace(/\)$/, "");
};

// Group a day's transactions by their source item, preserving first-seen order.
const groupByItem = (txns: Txn[]) => {
  const order: string[] = [];
  const groups: Record<string, { baseName: string; entries: Txn[] }> = {};

  txns.forEach((t) => {
    const key = t.itemId;
    if (!groups[key]) {
      groups[key] = { baseName: baseNameOf(t.name), entries: [] };
      order.push(key);
    }
    groups[key].entries.push(t);
  });

  return order.map((k) => groups[k]);
};

const PayBreakdown = ({ breakdown }: { breakdown: Txn["payBreakdown"] }) =>
  breakdown ? (
    <span style={{ opacity: 0.6, fontSize: "0.85em", marginLeft: 6 }}>
      ({breakdown.map((s) => `${s.days}d @ ${s.label}`).join(", ")})
    </span>
  ) : null;

export function Table2({
  transactions,
}: {
  transactions: PresentationalTransaction[];
}) {
  const [filterString, setFilterString] = useState<string>("");

  const visibleRows = transactions.flatMap((i) => {
    if (!filterString) return [{ day: i, visibleTransactions: i.transactions }];
    const lower = filterString.toLowerCase();
    const visibleTransactions = i.transactions.filter((t) =>
      t.name.toLowerCase().includes(lower),
    );
    if (visibleTransactions.length === 0) return [];
    return [{ day: i, visibleTransactions }];
  });

  const rows = visibleRows.map(({ day: i, visibleTransactions }) => {
    if (i === transactions[0]) {
      return (
        <Table.Tr opacity={1} key={i.date.toString()}>
          <Table.Td colSpan={1}>
            {i.date.equals(i.toDate) ? (
              i.date.toString()
            ) : (
              <div
                style={{
                  display: "inline-block",
                }}
              >
                <Stack align="center" gap={5}>
                  {i.date.toString()}
                  <ThemeIcon color="grey" size={iconHeight}>
                    <IconArrowDown />
                  </ThemeIcon>
                  {i.toDate.toString()}
                </Stack>
              </div>
            )}
          </Table.Td>
          <Table.Td colSpan={1}>
            <List>
              <List.Item icon={neutralIcon}>Initial balance</List.Item>
            </List>
          </Table.Td>
          <Table.Td>{formatCurrencyFromPence(i.closingBalancePence)}</Table.Td>
        </Table.Tr>
      );
    }

    if (visibleTransactions.length > 0) {
      return (
        <Table.Tr key={i.date.toString()}>
          <Table.Td>{i.date.toString()}</Table.Td>
          <Table.Td>
            {groupByItem(visibleTransactions).map((group) => {
              const total = group.entries.reduce(
                (sum, e) => sum + e.amountPence,
                0,
              );

              // Single-line item (no breakdown components).
              if (
                group.entries.length === 1 &&
                group.entries[0].name === group.baseName
              ) {
                const only = group.entries[0];
                return (
                  <List key={uniqueId()}>
                    <List.Item
                      icon={only.amountPence > 0 ? positiveIcon : negativeIcon}
                    >
                      {only.name}
                      {": "}
                      {formatCurrencyFromPence(only.amountPence)}
                      <PayBreakdown breakdown={only.payBreakdown} />
                    </List.Item>
                  </List>
                );
              }

              // Multi-component item: one header, indented sub-lines.
              return (
                <List key={uniqueId()}>
                  <List.Item icon={total > 0 ? positiveIcon : negativeIcon}>
                    {group.baseName}
                    {": "}
                    {formatCurrencyFromPence(total)}
                    <List withPadding listStyleType="disc" mt={2}>
                      {group.entries.map((e) => (
                        <List.Item key={uniqueId()}>
                          {labelFor(group.baseName, e.name)}
                          {": "}
                          {formatCurrencyFromPence(e.amountPence)}
                          <PayBreakdown breakdown={e.payBreakdown} />
                        </List.Item>
                      ))}
                    </List>
                  </List.Item>
                </List>
              );
            })}
          </Table.Td>
          <Table.Td>{formatCurrencyFromPence(i.closingBalancePence)}</Table.Td>
        </Table.Tr>
      );
    }

    return (
      <Table.Tr opacity={0.5} key={i.date.toJSON()}>
        <Table.Td colSpan={1}>
          {i.date.equals(i.toDate) ? (
            i.date.toString()
          ) : (
            <div
              style={{
                display: "inline-block",
              }}
            >
              <Stack align="center" gap={5}>
                {i.date.toString()}
                <ThemeIcon color="grey" size={iconHeight}>
                  <IconArrowDown />
                </ThemeIcon>
                {i.toDate.toString()}
              </Stack>
            </div>
          )}
        </Table.Td>
        <Table.Td colSpan={1}>
          <List>
            <List.Item icon={neutralIcon}>No expected transactions</List.Item>
          </List>
        </Table.Td>
        <Table.Td>{formatCurrencyFromPence(i.closingBalancePence)}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <>
      <TextInput
        onChange={(e) => setFilterString(e.target.value)}
        placeholder="Search by date or transaction"
        mb="md"
        mx="auto"
        style={{ width: "90%", maxWidth: "950px" }}
        leftSection={
          <IconSearch style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
        }
      />
    <Table
      withTableBorder
      highlightOnHover
      mx="auto"
      style={{ width: "90%", maxWidth: "950px" }}
      stickyHeader
      stickyHeaderOffset={60}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Date</Table.Th>
          <Table.Th>Transactions</Table.Th>
          <Table.Th>Balance</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
    </>
  );
}
